import React, { JSX, useEffect, useRef } from "react";
import {
  Animated,
  Dimensions,
  Easing,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { MasterProduct } from "../../models/master_product";
import { primaryColor } from "../../services/theme_service";
import { CardList } from "./card_list";
import { Footer } from "./footer";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Item } from "../../models/item_model";

// =========================
// Props
// =========================
interface HomeProps {
  products: MasterProduct[];
  celebItems: any[];
  topRated: any[];
  onShowMore: (isCelebrity: boolean) => void;
}

// =========================
// Main HomePage
// =========================
export const HomePage: React.FC<HomeProps> = ({
  products,
  celebItems,
  topRated,
  onShowMore,
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return isMobile ? (
    <MobileHomePage
      products={products}
      celebItems={celebItems}
      topRated={topRated}
      onShowMore={onShowMore}
      navigate={navigate}
    />
  ) : (
    <WebHomePage
      products={products}
      celebItems={celebItems}
      topRated={topRated}
      onShowMore={onShowMore}
      navigate={navigate}
    />
  );
};

// =========================
// Mobile Component
// =========================
const MobileHomePage = ({
  products,
  celebItems,
  topRated,
  onShowMore,
  navigate,
}: any) => {
  const animatedValues = useRef(
    Array.from({ length: 7 }, () => new Animated.Value(0))
  ).current;

  useEffect(() => {
    const animations = animatedValues.map((val, index) =>
      Animated.timing(val, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
        delay: index * 100,
      })
    );
    Animated.stagger(200, animations).start();
  }, []);

  const handleCategoryClick = (product: MasterProduct) => {
    navigate("/result", {
      state: {
        masterProductId: product.master_product_id,
        headerTitle: product.product_name,
      },
    });
  };

  // Masonry Card Component
  const MasonryCard = ({ item }: any) => (
    <View style={mobileStyles.masonryCard}>
      <Image
        source={{ uri: item.image_url }}
        style={mobileStyles.masonryImage}
        resizeMode="cover"
      />
      <Text style={mobileStyles.masonryCategory}>{item.product_name}</Text>
      <TouchableOpacity
        onPress={() => handleCategoryClick(item)}
        style={mobileStyles.shopNowButton}
      >
        <Text style={mobileStyles.shopNowText}>Shop More</Text>
      </TouchableOpacity>
    </View>
  );

  // Load master products from localStorage
  const masterProducts = (() => {
    try {
      const data = localStorage.getItem("headerProducts");
      if (data) return JSON.parse(data);
    } catch { }
    return [];
  })();

  // Responsive sizing
  const { width: screenWidth } = useWindowDimensions();
  const iconSize = screenWidth * 0.07;
  const cardPadding = screenWidth * 0.04;
  const fontSize = screenWidth * 0.038;
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const cards = [
    {
      title: "Free Shipping",
      icon: require("../../assets/icons/shipping_icon.png"),
      bg: "#F7E6EE",
    },
    {
      title: "Secure Payment",
      icon: require("../../assets/icons/payment_icon.png"),
      bg: "#DEF3F8",
    },
    {
      title: "Customer Service",
      icon: require("../../assets/icons/shipping_icon.png"),
      bg: "#DEF7F1",
    },
  ];

  return (
    <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
      {/* Horizontal Master Products */}
      {masterProducts.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ marginVertical: 10, paddingHorizontal: 10 }}
        >
          {masterProducts.map((prod: any) => (
            <TouchableOpacity
              key={prod.master_product_id}
              style={mobileStyles.mobileCategoryCard}
              onPress={() => handleCategoryClick(prod)}
            >
              <Image
                source={{ uri: prod.image_url }}
                style={mobileStyles.mobileCategoryImage}
              />
              <Text style={mobileStyles.mobileCategoryText}>
                {prod.product_name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      {/* Hero Image */}
      <Animated.View style={{ opacity: animatedValues[0] }}>
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={() =>
            navigate("/result", {
              state: {
                isCelebrityChoice: true,
                headerTitle: "The Festive Collection",
              },
            })
          }
        >
          <Image
            source={require("../../assets/images/home.png")}
            style={{ width: "100%", height: 250 }}
            resizeMode="cover"
          />
        </TouchableOpacity>
      </Animated.View>

      {/* Feature Cards */}
      <View style={mobileStyles.container}>
        {cards.map((card, index) => (
          <Animated.View
            key={index}
            style={[
              mobileStyles.card,
              { backgroundColor: card.bg, padding: cardPadding },
            ]}
          >
            <Image
              source={card.icon}
              style={{ width: iconSize, height: iconSize, marginBottom: 5 }}
              resizeMode="contain"
            />
            <Text
              style={{ fontSize, fontWeight: "600", textAlign: "center" }}
            >
              {card.title}
            </Text>
          </Animated.View>
        ))}
      </View>

      {/* Shop By Category */}
      <View
        style={{
          marginVertical: 30,
          alignItems: "center",
          paddingHorizontal: 10,
        }}
      >
        <Text style={{ fontSize: 28, fontWeight: "500", textAlign: "center" }}>
          Shop By Category
        </Text>
        <Text
          style={{
            fontSize: 16,
            textAlign: "center",
            marginTop: 8,
          }}
        >
          Discover Luxury: Premium & Authentic Sarees, a Treasure Trove of
          Handlooms in Vivid Weaves and Crafts, Crafted in Finest Silks.
        </Text>
      </View>

      {/* Masonry Cards */}
      {/* <View style={{ paddingHorizontal: 12 }}>
        {products.map((item: Item) => (
          <MasonryCard key={item.master_product_id} item={item} />
        ))}
      </View> */}

      {/* Celebrity & Top Rated */}
      <CardList
        title={"Style Like Celebrity"}
        items={celebItems}
        onShowMore={() => onShowMore(true)}
        isMobileView={isMobile}
      />
      <CardList
        title={"Top Rated"}
        items={topRated}
        onShowMore={() => onShowMore(false)}
        isMobileView={isMobile}
      />
      <View style={{ height: 20 }}></View>
      <Footer />
    </ScrollView>
  );
};

// =========================
// Web Component
// =========================
const WebHomePage = ({ products, celebItems, topRated, onShowMore, navigate }: any) => {
  const animatedValues = useRef(
    Array.from({ length: 7 }, () => new Animated.Value(0))
  ).current;

  useEffect(() => {
    const animations = animatedValues.map((val, index) =>
      Animated.timing(val, {
        toValue: 1,
        duration: 200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
        delay: index * 100,
      })
    );

    Animated.stagger(200, animations).start();
  }, []);

  const handleCategoryClick = (product: MasterProduct) => {
    navigate("/result", {
      state: {
        masterProductId: product.master_product_id,
        headerTitle: product.product_name,
      },
    });
  };

  const MasonryCard = ({ item, widthValue, flexValue, height, marginRight }: any) => (
    <View
      style={[
        webStyles.masonryCard,
        { width: widthValue, flex: flexValue, height, marginRight },
      ]}
    >
      <Image
        source={{ uri: item.image_url }}
        style={webStyles.masonryImage}
        resizeMode="cover"
      />
      <Text style={webStyles.masonryCategory}>{item.product_name}</Text>
      <TouchableOpacity
        onPress={() => handleCategoryClick(item)}
        style={webStyles.shopNowButton}
      >
        <Text style={webStyles.shopNowText}>Shop More</Text>
      </TouchableOpacity>
    </View>
  );

  const renderMasonry = (products: MasterProduct[]) => {
    const items: JSX.Element[] = [];
    for (let i = 0; i < products.length; i += 4) {
      const [card1, card2, card3, card4] = products.slice(i, i + 4);
      items.push(
        <View key={i} style={webStyles.masonryRow}>
          {card1 && (
            <MasonryCard item={card1} widthValue={width * 0.45} height={462} />
          )}
          <View style={webStyles.masonryColumn}>
            <View style={webStyles.masonrySubRow}>
              {card2 && (
                <MasonryCard
                  item={card2}
                  flexValue={1}
                  height={220}
                  marginRight={12}
                />
              )}
              {card3 && (
                <MasonryCard item={card3} flexValue={1} height={220} />
              )}
            </View>
            {card4 && (
              <MasonryCard item={card4} widthValue={"100%"} height={230} />
            )}
          </View>
        </View>
      );
    }
    return items;
  };

  return (
    <ScrollView contentContainerStyle={webStyles.scrollContainer}>
      <View style={{ height: 100 }}>
        {/* Hero Image */}
        <Animated.View
          style={[
            {
              opacity: animatedValues[0],
              transform: [
                {
                  translateY:
                    animatedValues[0]?.interpolate({
                      inputRange: [0, 1],
                      outputRange: [20, 0],
                    }) || 0,
                },
              ],
            },
          ]}
        >
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() =>
              navigate("/result", {
                state: {
                  isCelebrityChoice: true,
                  headerTitle: "The Festive Collection",
                },
              })
            }
          >
            <Image
              source={require("../../assets/images/home.png")}
              style={webStyles.heroImage}
              resizeMode="cover"
            />
          </TouchableOpacity>
        </Animated.View>

        {/* Feature Cards */}
        <View style={webStyles.featuresRow}>
          {[
            {
              title: "Free Shipping",
              subtitle: "Enjoy free shipping on all orders",
              icon: require("../../assets/icons/shipping_icon.png"),
              bg: "#F7E6EE",
            },
            {
              title: "Secure Payment",
              subtitle: "Pay with Multiple Payment methods",
              icon: require("../../assets/icons/payment_icon.png"),
              bg: "#DEF3F8",
            },
            {
              title: "Customer Service",
              subtitle: "We are available from Monday to Friday",
              icon: require("../../assets/icons/shipping_icon.png"),
              bg: "#DEF7F1",
            },
          ].map((card, index) => (
            <Animated.View
              key={index}
              style={[
                webStyles.featureCard,
                {
                  backgroundColor: card.bg,
                  opacity: animatedValues[index + 1],
                  transform: [
                    {
                      translateY:
                        animatedValues[index + 1]?.interpolate({
                          inputRange: [0, 1],
                          outputRange: [20, 0],
                        }) || 0,
                    },
                  ],
                },
              ]}
            >
              <View style={webStyles.featureContent}>
                <Image
                  source={card.icon}
                  style={webStyles.featureIcon}
                  resizeMode="contain"
                />
                <View>
                  <Text style={webStyles.featureTitle}>{card.title}</Text>
                  <Text style={webStyles.featureSubtitle}>{card.subtitle}</Text>
                </View>
              </View>
            </Animated.View>
          ))}
        </View>

        {/* Shop By Category */}
        <Animated.View
          style={[
            webStyles.shopByCategory,
            {
              opacity: animatedValues[4],
              transform: [
                {
                  translateY:
                    animatedValues[4]?.interpolate({
                      inputRange: [0, 1],
                      outputRange: [20, 0],
                    }) || 0,
                },
              ],
            },
          ]}
        >
          <Text style={webStyles.shopTitle}>Shop By Category</Text>
          <Text style={webStyles.shopSubtitle}>
            {
              "Discover Luxury: Premium & Authentic Sarees, a \nTreasure Trove of Handlooms in Vivid Weaves and\nCrafts, Crafted in Finest Silks."
            }
          </Text>
        </Animated.View>

        {/* Horizontal Masonry */}
        {/* <Animated.View
          style={[
            webStyles.masonryWrapper,
            {
              opacity: animatedValues[6],
              transform: [
                {
                  translateY:
                    animatedValues[6]?.interpolate({
                      inputRange: [0, 1],
                      outputRange: [20, 0],
                    }) || 0,
                },
              ],
            },
          ]}
        >
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={webStyles.masonryScroll}
          >
            {renderMasonry(products)}
          </ScrollView>
        </Animated.View> */}

        {/* Celebrity & Top Rated Cards */}
        <CardList
          title={"Style Like Celebrity"}
          items={celebItems}
          onShowMore={() => onShowMore(true)}
        />
        <View style={{ height: 5 }} />
        <CardList
       
          title={"Top Rated"}
          items={topRated}
        
          onShowMore={() => onShowMore(false)}
        />

        {/* Footer */}
        <View style={{ height: 60 }} />
        <Footer />
      </View>
    </ScrollView>
  );
};

// =========================
// Web Styles
// =========================
const { width } = Dimensions.get("window");

const webStyles = StyleSheet.create({
  scrollContainer: { flexGrow: 1 },
  heroImage: { width: "100%", height: 550 },

  featuresRow: {
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 40,
    marginHorizontal: "10%",
  },
  featureCard: { flex: 1, borderRadius: 15, padding: 20, marginHorizontal: 8 },
  featureContent: { flexDirection: "row", alignItems: "center", gap: 10 },
  featureIcon: { width: 40, height: 40 },
  featureTitle: { fontSize: 18, fontWeight: "600", color: "#333" },
  featureSubtitle: { fontSize: 16, color: "#555", marginTop: 5 },

  shopByCategory: {
    flexDirection: "row",
    marginVertical: 100,
    maxHeight: 100,
    width: "100%",
    paddingHorizontal: 12,
    alignContent: "center",
    justifyContent: "center",
    alignItems: "center",
  },
  shopTitle: { fontSize: 45, fontWeight: "500", color: "#111" },
  shopSubtitle: { fontSize: 30, marginLeft: 80, letterSpacing: 0.4 },

  masonryWrapper: {},
  masonryScroll: { paddingHorizontal: 12 },
  masonryRow: {
    flexDirection: "row",
    marginRight: 24,
    width: width * 0.95,
    paddingLeft: 50,
  },
  masonryColumn: { flex: 1, flexDirection: "column", marginLeft: 12 },
  masonrySubRow: { flexDirection: "row", marginBottom: 12 },
  masonryCard: {
    borderRadius: 12,
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#eee",
  },
  masonryImage: { width: "100%", height: "100%" },
  masonryCategory: {
    position: "absolute",
    top: 10,
    left: 10,
    color: "#fff",
    fontWeight: "700",
    fontSize: 16,
    backgroundColor: "rgba(0,0,0,0.4)",
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 4,
  },
  shopNowButton: {
    position: "absolute",
    bottom: 10,
    left: 10,
    backgroundColor: "#fff",
    paddingHorizontal: 25,
    paddingVertical: 8,
    borderRadius: 8,
  },
  shopNowText: { fontWeight: "600", color: primaryColor, fontSize: 16 },
});

// =========================
// Mobile Styles
// =========================
const { width: screenWidth } = Dimensions.get("window");
const imageSize = screenWidth * 0.15;
const fontSize = screenWidth * 0.035;


const mobileStyles = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginVertical: 25,
  },
  card: {
    borderRadius: 12,
    alignItems: "center",
    flex: 1,
    marginHorizontal: 5,
  },

  mobileCategoryCard: {
    backgroundColor: "white",
    borderRadius: 12,
    elevation: 3,
    marginRight: 8,
    alignItems: "center",
    padding: 10,

  },
  mobileCategoryImage: {
    width: imageSize,
    height: imageSize, borderRadius: 8, marginBottom: 8
  },
  mobileCategoryText: { fontSize, fontWeight: "500", textAlign: "center" },
  masonryCard: { borderRadius: 12, overflow: "hidden", position: "relative", backgroundColor: "#eee", marginBottom: 15 },
  masonryImage: { width: "100%", height: 200 },
  masonryCategory: {
    position: "absolute",
    top: 10,
    left: 10,
    color: "#fff",
    fontWeight: "700",
    fontSize: 14,
    backgroundColor: "rgba(0,0,0,0.4)",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
  },
  shopNowButton: {
    position: "absolute",
    bottom: 10,
    left: 10,
    backgroundColor: "#fff",
    paddingHorizontal: 20,
    paddingVertical: 6,
    borderRadius: 8,
  },
  shopNowText: { fontWeight: "600", color: primaryColor, fontSize: 14 },
});
